import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { expValEquals } from '@confluence/feature-experiments';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { EditorBreadcrumbsQuery } from './EditorBreadcrumbsQuery.experimentalgraphql';
import { handleEditorBreadcrumbsQueryResult } from './handleEditorBreadcrumbsQueryResult';
import { useExperimentalEditorBreadcrumbsQuery } from './useExperimentalEditorBreadcrumbsQuery';
import type {
	EditorBreadcrumbsQuery as EditorBreadcrumbsQueryType,
	EditorBreadcrumbsQueryVariables,
} from './__types__/EditorBreadcrumbsQuery';

export type BreadcrumbsType = Array<{
	url: string | null;
	title: string;
}>;

export function useEditorBreadcrumbs(
	contentId?: EditorBreadcrumbsQueryVariables['contentId'],
): BreadcrumbsType {
	const queryArgs = {
		variables: {
			contentId: contentId!,
		},
		skip: !contentId, // The template editor does not carry a contentId.
	};

	const { data, error } = expValEquals(
		'confluence_deprecate_experimental_queries_exp',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalEditorBreadcrumbsQuery(queryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<EditorBreadcrumbsQueryType, EditorBreadcrumbsQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				EditorBreadcrumbsQuery,
				queryArgs,
			);

	const breadcrumbs = handleEditorBreadcrumbsQueryResult(data, error);

	const memoizedBreadcrumbs = useMemo(() => {
		return handleEditorBreadcrumbsQueryResult(data, error);
	}, [data, error]);

	return expValEquals('confluence_edit_page_rerender_optimizations', 'isEnabled', true)
		? memoizedBreadcrumbs
		: breadcrumbs;
}
